import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useReplaceDocumentMutation, useCreateDocumentsMutation } from 'api/main';
import { docTypes } from 'utils/constants';
import {
    validateFileSize,
    validateFileType,
    getDocumentLabelByType,
    handleDocumentDownload,
} from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import IdDocumentDialog from './IdDocumentDialog';
import { ActionButton, FileInputButton, FileErrorMessage, Loader } from 'components';
import { Box, IconButton, Paper, Typography } from '@mui/material';
import * as icons from 'mdi-material-ui';

const DocumentSlot = ({
    userId,
    documents = [],
    documentType,
    documentSubtype,
    onSuccess,
    canManage,
    multiple = false,
    hideAddButton = false,
    hideDocuments = false,
}) => {
    const [
        createDocuments,
        { isLoading: createLoading, isSuccess: createSuccess, reset: resetCreate },
    ] = useCreateDocumentsMutation({
        fixedCacheKey: `create-${documentType}-${documentSubtype || ''}-${userId}`,
    });
    const [typeError, setTypeError] = useState('');
    const [sizeError, setSizeError] = useState('');
    const [idDocumentDialogOpen, setIdDocumentDialogOpen] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);

    const handleUploadDocument = useCallback(
        e => {
            const selectedFiles = e.target.files;
            if (!selectedFiles.length) return;

            const formData = new FormData();
            for (const selectedFile of selectedFiles) {
                if (validateFileSize(selectedFile)) {
                    setSizeError('');
                } else {
                    setSizeError('size');
                    return;
                }

                if (validateFileType(selectedFile, documentType, documentSubtype)) {
                    setTypeError('');
                } else {
                    setTypeError(texts.FILE_TYPE_INCORRECT);
                    return;
                }
                formData.append('files[]', selectedFile);
            }

            formData.append('type', documentType);
            if (documentSubtype) {
                formData.append('subtype', documentSubtype);
            }
            formData.append('belongsToUserId', userId);
            createDocuments({ formData });
        },
        [createDocuments, documentType, documentSubtype, userId]
    );

    useEffect(() => {
        if (createSuccess) {
            onSuccess?.();
        }
    }, [createSuccess, onSuccess]);

    useEffect(() => {
        if (createSuccess) {
            resetCreate();
            setSizeError('');
            setTypeError('');
        }
    }, [createSuccess, resetCreate]);

    const getAcceptedFileTypes = () => {
        const { ID } = docTypes;
        if (documentType === ID) return '.pdf, .jpeg, .jpg, .png, .bmp, .gif';
        return '.pdf, .jpeg, .jpg, .png, .bmp, .gif, .doc, .docx';
    };
    if (documentType === docTypes.RIB) {
        console.log(documents);
    }

    if (hideDocuments) {
        return (
            <Box>
                {canManage && !hideAddButton && (!documents.length || multiple) && (
                    <FileInputButton
                        id={documentType + (documentSubtype || '')}
                        sx={{ padding: '5px 8px' }}
                        loading={createLoading}
                        label={texts.UPLOAD_FILE_LABEL}
                        onFileChange={handleUploadDocument}
                        acceptedFileTypes={getAcceptedFileTypes()}
                        multiple={multiple}
                        noMargin
                    />
                )}
                {sizeError && <FileErrorMessage variant="size" useMargin={false} />}
                {typeError && <FileErrorMessage variant="type" useMargin={false} />}
            </Box>
        );
    }

    return (
        <Paper
            elevation={0}
            sx={{ p: 1.5, mb: 2, borderRadius: '5px', border: '1px solid #E0E0E0' }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        flex: '1 1',
                    }}
                >
                    {!documents.length && (
                        <Typography fontStyle={multiple ? 'italic' : 'normal'}>
                            {multiple
                                ? texts.NO_DOCUMENTS
                                : getDocumentLabelByType(documentType, documentSubtype)}
                        </Typography>
                    )}
                    {documents.map(doc => (
                        <Box
                            key={doc.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                            }}
                        >
                            <Typography>
                                {multiple && doc?.name
                                    ? doc?.name
                                    : getDocumentLabelByType(documentType, documentSubtype)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Loader size={20} marginTop={0} loading={downloadingId === doc.id}>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        sx={{ p: 0 }}
                                        onClick={() => {
                                            setDownloadingId(doc.id);
                                            handleDocumentDownload(doc, isLoading => {
                                                if (!isLoading) setDownloadingId(null);
                                            });
                                        }}
                                    >
                                        <icons.Download />
                                    </IconButton>
                                </Loader>
                                {canManage && (
                                    <ReplaceButton
                                        docId={doc.id}
                                        documentType={documentType}
                                        documentSubtype={documentSubtype}
                                        setSizeError={setSizeError}
                                        setTypeError={setTypeError}
                                        onSuccess={onSuccess}
                                        onIDAction={() => setIdDocumentDialogOpen(true)}
                                        acceptedFileTypes={getAcceptedFileTypes()}
                                    />
                                )}
                            </Box>
                        </Box>
                    ))}
                    {documentType === docTypes.ID && canManage && (
                        <IdDocumentDialog
                            open={idDocumentDialogOpen}
                            id={documents[0]?.id}
                            handleClose={() => setIdDocumentDialogOpen(false)}
                            userId={userId}
                            title={documents[0]?.id ? texts.REPLACE : texts.UPLOAD_FILE_LABEL}
                            subtype={documentSubtype}
                        />
                    )}
                </Box>
                {canManage &&
                !hideAddButton &&
                !documents.length &&
                documentType === docTypes.ID ? (
                    <ActionButton
                        variant="outlined"
                        sx={{ borderRadius: '5px', minWidth: '130px' }}
                        onClick={() => setIdDocumentDialogOpen(true)}
                    >
                        {texts.UPLOAD_FILE_LABEL}
                    </ActionButton>
                ) : (
                    !documents.length &&
                    !multiple &&
                    !hideAddButton && (
                        <FileInputButton
                            id={documentType + (documentSubtype || '')}
                            sx={{ padding: '5px 8px' }}
                            loading={createLoading}
                            label={texts.UPLOAD_FILE_LABEL}
                            onFileChange={handleUploadDocument}
                            acceptedFileTypes={getAcceptedFileTypes()}
                            multiple={multiple}
                            noMargin
                        />
                    )
                )}
            </Box>
            {sizeError && <FileErrorMessage variant="size" useMargin={false} />}
            {typeError && <FileErrorMessage variant="type" useMargin={false} />}
        </Paper>
    );
};

const ReplaceButton = ({
    docId,
    acceptedFileTypes,
    documentType,
    documentSubtype,
    onIDAction,
    setSizeError,
    setTypeError,
    onSuccess,
}) => {
    const [replaceDocument, { isLoading: replaceLoading, isSuccess, reset }] =
        useReplaceDocumentMutation({
            fixedCacheKey: `replace-${docId}`,
        });

    const handleReplaceDocument = useCallback(
        e => {
            const selectedFile = e.target.files[0];
            if (selectedFile) {
                if (validateFileSize(selectedFile)) {
                    setSizeError('');
                } else {
                    setSizeError('size');
                    return;
                }

                if (validateFileType(selectedFile, documentType, documentSubtype)) {
                    setTypeError('');
                } else {
                    setTypeError(texts.FILE_TYPE_INCORRECT);
                    return;
                }

                const formData = new FormData();
                formData.append('file', selectedFile);
                formData.append('type', documentType);
                if (documentSubtype) {
                    formData.append('subtype', documentSubtype);
                }
                replaceDocument({ id: docId, formData });
            }
        },
        [documentType, documentSubtype, replaceDocument, docId, setSizeError, setTypeError]
    );

    useEffect(() => {
        if (isSuccess) {
            reset();
            setSizeError('');
            setTypeError('');
            onSuccess?.();
        }
    }, [isSuccess, onSuccess, reset, setSizeError, setTypeError]);

    if (documentType === docTypes.ID)
        return (
            <ActionButton
                variant="outlined"
                sx={{ borderRadius: '5px', minWidth: '130px' }}
                onClick={onIDAction}
            >
                {texts.REPLACE}
            </ActionButton>
        );

    return (
        <FileInputButton
            id={docId}
            sx={{ padding: '5px 8px' }}
            loading={replaceLoading}
            label={texts.REPLACE}
            onFileChange={handleReplaceDocument}
            acceptedFileTypes={acceptedFileTypes}
            noMargin
        />
    );
};

ReplaceButton.propTypes = {
    docId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    onIDAction: PropTypes.func.isRequired,
    setSizeError: PropTypes.func.isRequired,
    setTypeError: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    acceptedFileTypes: PropTypes.string.isRequired,
    documentType: PropTypes.oneOf(Object.values(docTypes)),
    documentSubtype: PropTypes.string,
};

DocumentSlot.propTypes = {
    userId: PropTypes.number.isRequired,
    documents: PropTypes.array,
    documentType: PropTypes.oneOf(Object.values(docTypes)),
    documentSubtype: PropTypes.string,
    onSuccess: PropTypes.func,
    canManage: PropTypes.bool,
    multiple: PropTypes.bool,
    hideAddButton: PropTypes.bool,
    hideDocuments: PropTypes.bool,
};

export default DocumentSlot;
